// labiq's WheelActionController (C3/C5) — the confirm boundary for agent-proposed wheel authoring.
// Envelopes pinned against the REAL controller (feature-tested server-side), key for key.
import { ENV } from "../../config/runtimeEnv";
import type { WheelActionItemResult, WheelUndoItemResult } from "../../types/geneCodes/WheelActionTypes";
import { http } from "../../lib/http/client";

// VITE_LABIQ_SERVER already ends in /api — the GxEApi rule; a second /api 404s every call.
const API_BASE_URL = ENV.VITE_LABIQ_SERVER || "";

export interface WheelActionItem {
	action_type: string;
	// Sent VERBATIM from the proposal — the target trait is bound at proposal time, and rs case is
	// never normalised in JS: the server matches and reports what it skipped.
	params: Record<string, unknown>;
}

export interface ConfirmWheelActionsPayload {
	batch_id: string;
	/** Omissible ONLY when items[0] is create_wheel — the server 422s otherwise. */
	panel_id?: number;
	items: WheelActionItem[];
}

export interface ConfirmWheelActionsResult {
	batch_id: string;
	panel_id: number | null;
	/** Set when the target was published: the batch applied to the fork named by panel_id. */
	forked_from_panel_id?: number | null;
	confirmed_at: string;
	items: WheelActionItemResult[];
	/** A double-confirm returns the recorded body with this set — compare semantically, not by key order. */
	already_confirmed?: boolean;
}

/** The 422 body's failing_item — the batch rolled back and nothing was applied. */
export interface WheelActionFailingItem {
	index: number;
	action_type: string;
	errors: Record<string, string[]>;
}

/** POST /wheel-actions/confirm — 201 fresh, 200 + already_confirmed on a replayed batch_id. */
export const confirmWheelActions = async (payload: ConfirmWheelActionsPayload): Promise<ConfirmWheelActionsResult> => {
	const response = await http.post(`${API_BASE_URL}/wheel-actions/confirm`, payload);
	return response.data;
};

export interface UndoWheelActionsResult {
	batch_id: string;
	panel_id: number;
	undone_at: string;
	items: WheelUndoItemResult[];
	already_undone?: boolean;
}

/** POST /wheel-actions/{batchId}/undo — 200; 409 once the wheel was published/archived since. */
export const undoWheelActions = async (batchId: string): Promise<UndoWheelActionsResult> => {
	const response = await http.post(`${API_BASE_URL}/wheel-actions/${batchId}/undo`, {});
	return response.data;
};

/** WheelActionBatch::STATUS_* — a pending row is a confirm that never completed, not a confirmation. */
export type WheelActionBatchStatus = "pending" | "confirmed" | "undone";

/** WheelActionController::show()'s body. `undo_state` is deliberately absent — it may carry drafted values. */
export interface WheelActionBatchStatusResult {
	status: WheelActionBatchStatus;
	result: ConfirmWheelActionsResult | null;
	applied_panel_id: number | null;
	forked_from_panel_id: number | null;
	undo_result: UndoWheelActionsResult | null;
	already_undone: boolean;
}

/** GET /wheel-actions/{batchId} — 200 for a known batch; 404 is the "never confirmed" answer. */
export const fetchWheelActionBatch = async (batchId: string): Promise<WheelActionBatchStatusResult> => {
	const response = await http.get(`${API_BASE_URL}/wheel-actions/${batchId}`);
	return response.data;
};
